/**
 * Cookie banner — consenso granulare (DSGVO + § 25 TTDSG).
 *
 * Concept di serie chiamava `setTrackingConsent(true|false)`: un booleano che
 * accende o spegne tutto insieme. In Germania non basta —
 *   1. § 25 TTDSG vuole il consenso per CATEGORIA, non in blocco;
 *   2. "Rifiuta tutto" deve essere raggiungibile con un clic e avere lo stesso
 *      peso visivo di "Accetta tutto" (giurisprudenza tedesca consolidata);
 *   3. l'utente deve poter cambiare idea dopo → link "Cookie-Einstellungen".
 *
 * Qui passiamo l'oggetto granulare all'API Customer Privacy di Shopify, che
 * registra il consenso lato Shopify (traccia di audit) e lo propaga a Consent
 * Mode v2. Nessuno script di terzi, nessuna chiamata esterna.
 *
 * Vedi VERSION-DD.md.
 */
if (!customElements.get('cookie-banner')) {
  customElements.define(
    'cookie-banner',
    class CookieBanner extends ModalElement {
      constructor() {
        super();

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        } else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }

      get acceptButton() { return this.querySelector('button[name="accept"]'); }
      get declineButton() { return this.querySelector('button[name="decline"]'); }
      get prefsButton() { return this.querySelector('button[name="preferences"]'); }
      get saveButton() { return this.querySelector('button[name="save-preferences"]'); }
      get prefsPanel() { return this.querySelector('[data-cookie-prefs]'); }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        // Il link "Cookie-Einstellungen" (footer o qualunque punto) riapre il
        // banner: senza una via di ritorno il consenso non e revocabile.
        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-cookie-settings]');
          if (!trigger) return;
          e.preventDefault();
          this.openPreferences();
          this.show();
        });

        if (this.testMode) {
          this.load(this.delay);
          return;
        }

        window.Shopify.loadFeatures([{
          name: 'consent-tracking-api',
          version: '0.1',
          onLoad: this.onConsentLibraryLoaded.bind(this)
        }]);
      }

      load(delay) {
        if (Shopify.designMode) return;

        setTimeout(() => this.show(), delay * 1000);
        this.bind();
      }

      bind() {
        if (this.bound) return;
        this.bound = true;
        if (this.acceptButton) this.acceptButton.addEventListener('click', this.acceptAll.bind(this));
        if (this.declineButton) this.declineButton.addEventListener('click', this.declineAll.bind(this));
        if (this.prefsButton) this.prefsButton.addEventListener('click', this.openPreferences.bind(this));
        if (this.saveButton) this.saveButton.addEventListener('click', this.saveSelection.bind(this));
      }

      openPreferences() {
        this.bind();
        const panel = this.prefsPanel;
        if (panel) panel.hidden = false;
        if (this.prefsButton) this.prefsButton.hidden = true;
      }

      /** Legge le caselle. I necessari non sono negoziabili e non hanno casella. */
      readChoices() {
        const get = (cat) => {
          const el = this.querySelector(`input[data-consent="${cat}"]`);
          return el ? !!el.checked : false;
        };
        return {
          analytics: get('analytics'),
          marketing: get('marketing'),
          preferences: get('preferences')
        };
      }

      apply(consent) {
        const payload = {
          analytics: !!consent.analytics,
          marketing: !!consent.marketing,
          preferences: !!consent.preferences,
          // Nessuna vendita di dati: sempre negato, non e una scelta dell'utente.
          sale_of_data: false
        };
        window.Shopify.customerPrivacy?.setTrackingConsent(payload, () => {
          document.dispatchEvent(new CustomEvent('dd:consent-updated', { detail: payload }));
        });
        this.hide();
      }

      acceptAll() {
        this.querySelectorAll('input[data-consent]').forEach((el) => { el.checked = true; });
        this.apply({ analytics: true, marketing: true, preferences: true });
      }

      declineAll() {
        this.querySelectorAll('input[data-consent]').forEach((el) => { el.checked = false; });
        this.apply({ analytics: false, marketing: false, preferences: false });
      }

      saveSelection() {
        this.apply(this.readChoices());
      }

      onConsentLibraryLoaded() {
        const privacy = window.Shopify.customerPrivacy;
        const decision = privacy.getTrackingConsent();

        // Ripristina le caselle su quanto gia scelto, cosi riaprendo le
        // preferenze l'utente rivede la sua configurazione e non un default.
        try {
          const current = privacy.currentVisitorConsent?.() || {};
          const set = (cat, val) => {
            const el = this.querySelector(`input[data-consent="${cat}"]`);
            if (el) el.checked = String(val).toLowerCase() === 'yes' || val === true;
          };
          set('analytics', current.analytics);
          set('marketing', current.marketing);
          set('preferences', current.preferences);
        } catch (e) { /* prima visita: restano i default del markup */ }

        if (decision === 'no_interaction') {
          this.load(this.delay);
        } else {
          this.bind();
        }
      }

      afterHide() {
        super.afterHide();
        document.body.classList.remove('has-cookie-banner');
      }

      afterShow() {
        super.afterShow();
        document.body.classList.add('has-cookie-banner');
      }
    }
  );
}
